import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import '../styles/Leagues.css';

function Leagues() {
  const { token, user, logout } = useAuth();
  const sportConfig = useSportConfig();
  const navTitle = sportConfig ? `${sportConfig.appEmoji} ${sportConfig.appName}` : '…';
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create league form
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMax, setNewMax] = useState(10);
  const [newBudget, setNewBudget] = useState(175);
  const [newDraftType, setNewDraftType] = useState('open');
  const [newSnakePickTime, setNewSnakePickTime] = useState(5 * 60 * 60 * 1000);
  const [newHeroMode, setNewHeroMode] = useState(false);
  const [newHeroPoolSize, setNewHeroPoolSize] = useState(5);
  const [selectedTournament, setSelectedTournament] = useState('');
  const [tournaments, setTournaments] = useState([]);

  // Join league form
  const [showJoin, setShowJoin] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  const [formError, setFormError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchLeagues();
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      const res = await fetch(`${API_URL}/tournaments?upcoming=true`);
      const data = await res.json();
      if (data.success) {
        setTournaments(data.tournaments);
      }
    } catch (err) {
      // tournaments will just be empty
    }
  };

  const fetchLeagues = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/leagues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setLeagues(data.leagues);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to load leagues');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      const res = await fetch(`${API_URL}/leagues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newName, maxManagers: newMax, draftBudget: newBudget, tournament: selectedTournament, draftType: newDraftType, snakePickTime: newSnakePickTime, heroMode: newHeroMode, heroPoolSize: newHeroPoolSize }),
      });
      const data = await res.json();
      if (data.success) {
        setLeagues([data.league, ...leagues]);
        setShowCreate(false);
        setNewName('');
        setNewMax(10);
        setNewBudget(175);
        setNewDraftType('open');
        setNewSnakePickTime(5 * 60 * 60 * 1000);
        setNewHeroMode(false);
        setNewHeroPoolSize(5);
        setSelectedTournament('');
      } else {
        setFormError(data.message);
      }
    } catch (err) {
      setFormError('Failed to create league');
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      const res = await fetch(`${API_URL}/leagues/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ joinCode }),
      });
      const data = await res.json();
      if (data.success) {
        setLeagues([data.league, ...leagues]);
        setShowJoin(false);
        setJoinCode('');
      } else {
        setFormError(data.message);
      }
    } catch (err) {
      setFormError('Failed to join league');
    }
  };

  return (
    <div className="leagues-container">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="nav-title">{navTitle}</h1>
        </div>
        <div className="nav-right">
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
          <button onClick={logout} className="btn btn-logout">Logout</button>
        </div>
      </nav>

      <div className="leagues-content">
        <header className="leagues-header">
          <h2>📊 My Leagues</h2>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => { setShowCreate(true); setShowJoin(false); setFormError(null); }}>
              + Create League
            </button>
            <button className="btn btn-accent" onClick={() => { setShowJoin(true); setShowCreate(false); setFormError(null); }}>
              🔑 Join League
            </button>
          </div>
        </header>

        {/* Create League Form */}
        {showCreate && (
          <div className="league-form-card">
            <h3>Create a New League</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>League Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Office Tennis League"
                  required
                />
              </div>
              <div className="form-group">
                <label>Tournament</label>
                <select
                  value={selectedTournament}
                  onChange={(e) => setSelectedTournament(e.target.value)}
                  required
                >
                  <option value="">Select a tournament...</option>
                  {tournaments.map(t => (
                    <option key={t._id} value={t._id}>
                      {t.name} — {t.surface} — {new Date(t.startDate).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Draft Type</label>
                <select value={newDraftType} onChange={(e) => setNewDraftType(e.target.value)}>
                  <option value="open">Open Draft — anyone picks any time</option>
                  <option value="snake">Snake Draft — turn-based, alternating order</option>
                </select>
              </div>
              {newDraftType === 'snake' && (
                <div className="form-group">
                  <label>Time Per Pick</label>
                  <select
                    value={newSnakePickTime}
                    onChange={(e) => setNewSnakePickTime(Number(e.target.value))}
                  >
                    <option value={30 * 60 * 1000}>30 minutes</option>
                    <option value={60 * 60 * 1000}>1 hour</option>
                    <option value={2 * 60 * 60 * 1000}>2 hours</option>
                    <option value={5 * 60 * 60 * 1000}>5 hours (default)</option>
                    <option value={12 * 60 * 60 * 1000}>12 hours</option>
                    <option value={24 * 60 * 60 * 1000}>24 hours</option>
                    <option value={48 * 60 * 60 * 1000}>48 hours</option>
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>⚡ Hero Mode</label>
                <select
                  value={newHeroMode ? 'enabled' : 'disabled'}
                  onChange={(e) => setNewHeroMode(e.target.value === 'enabled')}
                >
                  <option value="disabled">Disabled — standard draft</option>
                  <option value="enabled">Enabled — each manager gets one free hero pick</option>
                </select>
              </div>
              {newHeroMode && (
                <div className="form-group">
                  <label>Hero Pool Size</label>
                  <select value={newHeroPoolSize} onChange={(e) => setNewHeroPoolSize(Number(e.target.value))}>
                    <option value={3}>Top 3 per tour (6 heroes total)</option>
                    <option value={5}>Top 5 per tour (10 heroes total)</option>
                    <option value={10}>Top 10 per tour (20 heroes total)</option>
                  </select>
                  <p className="form-hint">Heroes are free, score normally, and multiple managers can share the same hero. Each manager's first pick is their hero.</p>
                </div>
              )}
              <div className="form-group">
                <label>Draft Budget (credits per manager)</label>
                <input
                  type="number"
                  value={newBudget}
                  onChange={(e) => setNewBudget(parseInt(e.target.value))}
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Max Managers</label>
                <input
                  type="number"
                  value={newMax}
                  onChange={(e) => setNewMax(parseInt(e.target.value))}
                  min="2"
                  max="20"
                />
              </div>
              {formError && <p className="form-error">{formError}</p>}
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Create</button>
                <button type="button" className="btn btn-cancel" onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Join League Form */}
        {showJoin && (
          <div className="league-form-card">
            <h3>Join a League</h3>
            <form onSubmit={handleJoin}>
              <div className="form-group">
                <label>Enter Join Code</label>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="e.g. A1B2C3"
                  maxLength={6}
                  required
                />
              </div>
              {formError && <p className="form-error">{formError}</p>}
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Join</button>
                <button type="button" className="btn btn-cancel" onClick={() => setShowJoin(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* League List */}
        {loading && <p className="status-msg">Loading leagues...</p>}
        {error && <p className="status-msg error">{error}</p>}

        {!loading && !error && leagues.length === 0 && (
          <div className="empty-state">
            <p>You're not in any leagues yet.</p>
            <p>Create one to become a commissioner, or join one with a code!</p>
          </div>
        )}

        {!loading && !error && leagues.length > 0 && (
          <div className="leagues-grid">
            {leagues.map(league => {
              const isCommissioner = league.commissioner._id === user?._id
                || (league.coCommissioners || []).some(c => c._id === user?._id);
              return (
                <Link to={`/leagues/${league._id}`} key={league._id} className="league-card-link">
                  <div className="league-card">
                    <div className="league-card-header">
                      <h3>{league.name}</h3>
                      {isCommissioner && <span className="commissioner-badge">Commissioner</span>}
                    </div>
                    <div className="league-card-info">
                      <p>{sportConfig?.appEmoji || '🏆'} {league.tournament?.name || 'No tournament'}</p>
                      <p>👥 {league.managers.length} / {league.maxManagers} managers</p>
                      <p>� Budget: {league.draftBudget || 175} credits</p>
                      <p>�📋 Status: {league.draftStatus}</p>                      <p>🐍 Draft: {league.draftType === 'snake' ? 'Snake' : 'Open'}</p>                      <p>🏅 Commissioner: {league.commissioner.firstName || league.commissioner.username}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Leagues;
